import {
  Alert,
  Button,
  Header,
  SpaceBetween,
} from "@amzn/awsui-components-console";
import React, { useCallback, useEffect, useState } from "react";

import useAdminCachedMessageById from "@/hooks/admin/useAdminCachedMessageById";
import useAdminGetUsers from "@/hooks/admin/useAdminGetUsers";

import CacheButton from "../buttons/CacheButton";
import LoadingSpinner from "../LoadingSpinner";
import ConversationContextSection from "./sections/ConversationContextSection";
import MessageDetailsSection from "./sections/MessageDetailsSection";
import SQLQuerySection from "./sections/SQLQuerySection";
import { MessageItem } from "./types";

export interface CachedMessageDetailsProps {
  messageId: string;
}

const CachedMessageDetails: React.FC<CachedMessageDetailsProps> = ({
  messageId,
}) => {
  const { message, loading, error, refresh } = useAdminCachedMessageById({
    messageId,
  });

  /**
   * The entry an accepted edit returned, rendered until the refetch replaces it.
   *
   * Every field shown here is server-sourced: the SQL, the timestamp, the
   * attribution and the cache status all come out of the response. Nothing is
   * constructed in the browser and nothing is taken from the modal's field
   * contents, which is what makes a response whose stored SQL differs from the
   * submitted text render the *stored* one.
   *
   * It exists alongside `refresh` rather than instead of it so the card is
   * correct immediately, without a spinner between the write and the refetch.
   */
  const [adopted, setAdopted] = useState<MessageItem | null>(null);

  /**
   * Release the adopted entry once the refetch has caught up with it -- but not
   * before.
   *
   * The naive form of this is `setAdopted(null)` on any change to `message`, and
   * it is wrong. The single-entry read runs a DynamoDB `Query` without
   * `ConsistentRead`, so it is eventually consistent: a refetch issued straight
   * after the write can legitimately return the *pre-edit* item. Clearing
   * unconditionally would then replace the entry the write itself returned --
   * which is strongly consistent, being that write's own `ALL_NEW` output --
   * with a stale one, and the admin would watch their accepted edit revert.
   *
   * So the adopted entry is only released when the refetched one is at least as
   * new. `lastUpdated` is written by one writer in one format, so comparing the
   * strings is comparing the instants. A refetch that comes back older is
   * ignored and the next one gets another chance.
   */
  useEffect(() => {
    if (!message) {
      return;
    }
    setAdopted((current) => {
      if (!current) {
        return null;
      }
      const refetched = message.lastUpdated ?? "";
      const held = current.lastUpdated ?? "";
      return refetched >= held ? null : current;
    });
  }, [message]);

  const handleSqlUpdated = useCallback(
    (entry: MessageItem) => {
      setAdopted(entry);
      refresh();
    },
    [refresh],
  );

  const shown = adopted ?? message;

  const { loading: usersLoading, items: users } = useAdminGetUsers();
  const sender = users?.find((user) => user.userId === shown?.userId);

  return (
    <SpaceBetween size="xl">
      <Header
        variant="h1"
        actions={
          <SpaceBetween direction="horizontal" size="s">
            <Button
              variant="icon"
              iconName="refresh"
              data-testid="refresh-button"
              onClick={refresh}
              disabled={loading}
            />
            <CacheButton
              message={shown}
              loading={loading}
              // This page shows one entry, so refetching it is already the
              // cheapest correct update and stays authoritative -- unlike the
              // list, which would page a whole table scan.
              onSettled={refresh}
            />
          </SpaceBetween>
        }
      >
        {messageId}
      </Header>
      {(loading && !adopted) || usersLoading ? (
        <LoadingSpinner />
      ) : shown && sender ? (
        <SpaceBetween size="m">
          {error && <Alert type="error">{error}</Alert>}
          <MessageDetailsSection message={shown} sender={sender} />
          <SQLQuerySection
            query={shown.sqlQuery}
            messageId={messageId}
            onSqlUpdated={handleSqlUpdated}
          />
          <ConversationContextSection message={shown} sender={sender} />
        </SpaceBetween>
      ) : (
        error && <Alert type="error">{error}</Alert>
      )}
    </SpaceBetween>
  );
};

export default CachedMessageDetails;
